/**
 * PicksSection Component
 * "Betting Preferences" section matching mobile's layout:
 * - Preferred Sportsbook selector, and the EuroLeague book
 * - Auto-Apply Picks toggle
 * - Analysis Factors link (opens config panel)
 */

import { SportsbookSelector } from '@components/ui/SportsbookSelector';
import type { User, UserPreferences } from '@contexts/AuthContext';
import type { LeagueKey } from '@parlay-picker/shared/league';
import {
  isEuGameMarketSportsbookId,
  isSportsbook,
  sportsbookForLeague,
} from '@parlay-picker/shared/sportsbook';
import { useGenerationStore } from '@stores/generationStore';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';

const EUROLEAGUE: LeagueKey = 'basketball_euroleague';

interface PicksSectionProps {
  user: User | null;
  onUpdatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
}

export function PicksSection({ user, onUpdatePreferences }: PicksSectionProps): ReactElement {
  const setAutoApplyPicks = useGenerationStore((s) => s.setAutoApplyPicks);
  const openConfigPanel = useGlobalShortcutsStore((s) => s.openConfigPanel);

  const isAutoApplyEnabled = user?.preferences?.autoApplyPicks ?? false;
  const selectedSportsbook = user?.preferences?.preferredSportsbook ?? 'fanduel';

  const handleAutoApplyToggle = () => {
    const newValue = !isAutoApplyEnabled;
    setAutoApplyPicks(newValue);
    onUpdatePreferences({ autoApplyPicks: newValue });
  };

  const handleSportsbookChange = (sportsbook: string) => {
    if (isSportsbook(sportsbook)) onUpdatePreferences({ preferredSportsbook: sportsbook });
  };

  const euroleagueSportsbook = sportsbookForLeague(user?.preferences, EUROLEAGUE);
  const handleEuroleagueSportsbookChange = (sportsbook: string) => {
    if (isEuGameMarketSportsbookId(sportsbook)) {
      onUpdatePreferences({ preferredEuSportsbook: sportsbook });
    }
  };

  return (
    <section className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Betting Preferences</h3>
      <div className="bg-theme-surface rounded-lg border border-theme-border divide-y divide-theme-border">
        {/* Preferred Sportsbook */}
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-theme-text">Preferred Sportsbook</div>
              <div className="text-xs text-theme-text-tertiary mt-0.5">
                Used for odds and line comparison
              </div>
            </div>
            <SportsbookSelector
              selected={selectedSportsbook}
              onChange={handleSportsbookChange}
              openUpward
            />
          </div>
        </div>

        {/* EuroLeague Sportsbook */}
        <div className="p-4" data-testid="settings-euroleague-sportsbook">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-theme-text">EuroLeague Sportsbook</div>
              <div className="text-xs text-theme-text-tertiary mt-0.5">
                Used for EuroLeague picks and odds
              </div>
            </div>
            <SportsbookSelector
              selected={euroleagueSportsbook}
              onChange={handleEuroleagueSportsbookChange}
              openUpward
              leagueKey={EUROLEAGUE}
            />
          </div>
        </div>

        {/* Auto-Apply Picks */}
        <label className="flex items-center justify-between cursor-pointer p-4">
          <div>
            <div className="text-sm font-medium text-theme-text">Auto-Apply Picks</div>
            <div className="text-xs text-theme-text-tertiary mt-0.5">
              Automatically replace current picks when new ones are generated
            </div>
          </div>
          <button
            type="button"
            onClick={handleAutoApplyToggle}
            className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${
              isAutoApplyEnabled ? 'bg-theme-primary' : 'bg-theme-border'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                isAutoApplyEnabled ? 'translate-x-5' : ''
              }`}
            />
          </button>
        </label>

        {/* Analysis Factors */}
        <button
          type="button"
          onClick={openConfigPanel}
          className="flex items-center justify-between w-full p-4 hover:bg-theme-surface-hover transition-colors text-left"
        >
          <div>
            <div className="text-sm font-medium text-theme-text">Analysis Factors</div>
            <div className="text-xs text-theme-text-tertiary mt-0.5">
              Configure scoring weights for AI picks
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-theme-text-tertiary" />
        </button>
      </div>
    </section>
  );
}
